import React, { useEffect, useState } from 'react';
import { ArrowRight, BookOpen, Clock3, Network, PenTool, PlusCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import CardMetadata from '../../components/common/CardMetadata';
import { listCustomHLDWorkspaces, type CustomHLDWorkspace } from '../../data/hld/customHLDWorkspaces';
import { hldSessionService } from '../../services/hldSessionService';
import { toCustomHLDWorkspace } from '../../data/hld/customHLDWorkspaces';

const HLDPracticeHub: React.FC = () => {
  const [customWorkspaces, setCustomWorkspaces] = useState<CustomHLDWorkspace[]>([]);

  useEffect(() => {
    setCustomWorkspaces(listCustomHLDWorkspaces());
    hldSessionService.listCustomWorkspaces().then((sessions) => {
      const remote = sessions.flatMap((session) => session.customBrief ? [toCustomHLDWorkspace(session.customBrief, session.problemId, new Date(session.createdAt ?? Date.now()).getTime(), new Date(session.updatedAt ?? Date.now()).getTime())] : []);
      setCustomWorkspaces((local) => [...remote, ...local.filter((item) => !remote.some((cloud) => cloud.id === item.id))]);
    }).catch(() => { /* Local workspaces are still available offline. */ });
  }, []);

  return (
    <div className="nav-tab-page practice-page min-h-screen pt-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        badge="Practice Arena"
        title="HLD System Design"
        description="Build the reasoning and communication habits used to design reliable systems at scale."
        icon={Network}
      />

      <section className="mb-8" aria-labelledby="continue-designing-heading">
        <div className="mb-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">Your practice</p>
            <h2 id="continue-designing-heading" className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Saved workspaces</h2>
          </div>
          <Link to="/practice/hld/custom/new" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200">
            <PlusCircle className="h-4 w-4" /> New brief
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {customWorkspaces.slice(0, 3).map((workspace) => (
            <Link key={workspace.id} to={`/practice/hld/custom/${workspace.id}?mode=Guided`} className="group card flex items-center gap-4 p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><Clock3 className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-bold text-gray-900 dark:text-white">{workspace.title}</p><p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Your custom design brief · {workspace.estimatedTimeMinutes} min</p></div>
              <ArrowRight className="h-5 w-5 shrink-0 text-primary-600 transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="explore-hld-heading" className="border-t border-gray-200 pt-8 dark:border-gray-800">
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">Start designing</p>
          <h2 id="explore-hld-heading" className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Choose your HLD path</h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Learn the foundations, solve a guided design problem, or create a custom interview brief.</p>
        </div>
        <div className="practice-grid grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <Link
          to="/practice/hld/fundamentals/scale-from-zero-to-millions"
          className="catalog-card group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-4 focus-visible:ring-offset-gray-900"
        >
          <div className="practice-card-header flex items-start gap-3 mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-primary-100 to-sky-100 dark:from-primary-900/30 dark:to-sky-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-8 h-8 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">HLD Fundamentals</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mt-1.5">
                Start with how a simple application becomes a resilient system as users, data, and failure pressure grow.
              </p>
            </div>
          </div>
          <CardMetadata items={[{ label: 'Chapters', value: '3 articles' }, { label: 'Study time', value: '2.5–3.5 hrs' }]} />
          <div className="catalog-card__footer practice-card-footer flex items-center justify-between gap-3 pt-4 mt-auto border-t border-gray-100 dark:border-gray-800">
            <span className="practice-difficulty px-2.5 py-1 rounded-md text-[11px] font-semibold badge-green">Foundation</span>
            <span className="flex items-center text-primary-600 dark:text-primary-400 group-hover:translate-x-1 transition-transform rounded-md">
              <span className="text-sm font-medium mr-1">Start learning</span>
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>

        <Link
          to="/practice/hld/problems/rate-limiter"
          className="catalog-card group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-4 focus-visible:ring-offset-gray-900"
        >
          <div className="practice-card-header flex items-start gap-3 mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-violet-100 to-fuchsia-100 dark:from-violet-900/30 dark:to-fuchsia-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
              <PenTool className="w-8 h-8 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Practice Problems</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mt-1.5">
                Study a problem, then build and review an architecture in a guided design workspace.
              </p>
            </div>
          </div>
          <CardMetadata items={[{ label: 'Problems', value: '12 chapters' }, { label: 'Study time', value: '8–10.5 hrs' }]} />
          <div className="catalog-card__footer practice-card-footer flex items-center justify-between gap-3 pt-4 mt-auto border-t border-gray-100 dark:border-gray-800">
            <span className="practice-difficulty px-2.5 py-1 rounded-md text-[11px] font-semibold badge-yellow">Intermediate</span>
            <span className="flex items-center text-primary-600 dark:text-primary-400 group-hover:translate-x-1 transition-transform rounded-md">
              <span className="text-sm font-medium mr-1">Open problem</span>
              <ArrowRight className="w-4 h-4" />
            </span>
          </div>
        </Link>

        <Link
          to="/practice/hld/custom/new"
          className="catalog-card group card p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-4 focus-visible:ring-offset-gray-900"
        >
          <div className="practice-card-header flex items-start gap-3 mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-xl flex items-center justify-center flex-shrink-0"><PlusCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" /></div>
            <div className="flex-1 min-w-0 pt-0.5"><h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Custom Workspace</h2><p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2 mt-1.5">Create your own interviewer brief, constraints, time limit, and practice style.</p></div>
          </div>
          <CardMetadata items={[{ label: 'Brief', value: 'Your own prompt' }, { label: 'Modes', value: 'Guided, interview, test' }]} />
          <div className="catalog-card__footer practice-card-footer flex items-center justify-between gap-3 pt-4 mt-auto border-t border-gray-100 dark:border-gray-800"><span className="practice-difficulty px-2.5 py-1 rounded-md text-[11px] font-semibold badge-green">Personal</span><span className="flex items-center text-primary-600 dark:text-primary-400 group-hover:translate-x-1 transition-transform rounded-md"><span className="text-sm font-medium mr-1">Create workspace</span><ArrowRight className="w-4 h-4" /></span></div>
        </Link>
        </div>
      </section>
    </div>
    </div>
  );
};

export default HLDPracticeHub;
